import { useState } from 'react';
import {
  CircleStackIcon,
  HeartIcon,
  DocumentTextIcon,
  BeakerIcon,
  ShareIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/16/solid';
import useText from '@/hooks/useText';
import SharedBadge from '@/global/Badge/Badge';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { GoInfo } from 'react-icons/go';

type ComponentWithSources = { sources: string[]; count: number; weight: number };
type LiteratureComponent = { sources?: string[]; publications: number; weight: number };

export interface ScoreBreakdownData {
  total: number;
  components: {
    curated?: ComponentWithSources | null;
    clinical?: ComponentWithSources | null;
    inferred?: ComponentWithSources | null;
    models?: ComponentWithSources | null;
    biobank?: ComponentWithSources | null;
    literature?: LiteratureComponent | null;
  };
}

export type ScoreBreakdownProps = {
  data: ScoreBreakdownData;
};

const COMPONENT_CONFIG = [
  {
    key: 'curated' as const,
    label: 'Curated',
    hex: '#0a104f',
    textHex: '#0a104f',
    Icon: CircleStackIcon,
  },
  {
    key: 'clinical' as const,
    label: 'Clinical',
    hex: '#be0080',
    textHex: '#be0080',
    Icon: HeartIcon,
  },
  {
    key: 'literature' as const,
    label: 'Literature',
    hex: '#B45309',
    textHex: '#B45309',
    Icon: DocumentTextIcon,
  },
  {
    key: 'models' as const,
    label: 'Models',
    hex: '#10B981',
    textHex: '#047857',
    Icon: BeakerIcon,
  },
  {
    key: 'inferred' as const,
    label: 'Inferred',
    hex: '#8B5CF6',
    textHex: '#6D28D9',
    Icon: ShareIcon,
  },
  {
    key: 'biobank' as const,
    label: 'Biobank',
    hex: '#0D9488',
    textHex: '#0F766E',
    Icon: ArchiveBoxIcon,
  },
];

function getWeight(data: ScoreBreakdownData, key: (typeof COMPONENT_CONFIG)[number]['key']): number {
  return data.components[key]?.weight ?? 0;
}

function SegmentTooltip({ data, cfg }: { data: ScoreBreakdownData; cfg: (typeof COMPONENT_CONFIG)[number] }) {
  const { key, label, Icon } = cfg;
  const comp = data.components[key];
  const weight = comp?.weight ?? 0;
  const sources = comp != null && 'sources' in comp ? comp.sources : [];
  const publications = comp != null && 'publications' in comp ? (comp as any).publications : undefined;

  return (
    <div style={{ minWidth: 160 }}>
      <div className="flex items-center gap-1 font-semibold mb-1">
        <Icon className="w-3 h-3" />
        <span className="uppercase text-xs tracking-wider">{label}</span>
      </div>
      {sources.length > 0 && (
        <div className="text-xs text-gray-300 mb-0.5">
          Sources: {sources.join(', ')} ({sources.length})
        </div>
      )}
      {publications !== undefined && publications > 0 && (
        <div className="text-xs text-gray-300 mb-0.5">Publications: {publications}</div>
      )}
      {sources.length === 0 && publications === undefined && (
        <div className="text-xs text-gray-400 mb-0.5">No sources</div>
      )}
      <div className="text-xs font-semibold text-white mt-1">Weight: {weight.toFixed(2)}</div>
    </div>
  );
}

function SegmentedBar({ data }: { data: ScoreBreakdownData }) {
  const total = data.total;
  const activeSegments = COMPONENT_CONFIG.filter(cfg => getWeight(data, cfg.key) > 0);

  if (activeSegments.length === 0) {
    return (
      <div className="h-8 w-full rounded-md border border-gray-200 bg-gray-100 flex items-center justify-center">
        <span className="text-gray-400 text-xs">No score data</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex h-8 w-full border border-gray-200 rounded-md shadow-inner">
        {activeSegments.map((cfg, idx) => {
          const weight = getWeight(data, cfg.key);
          const widthPct = (weight / total) * 100;
          const isFirst = idx === 0;
          const isLast = idx === activeSegments.length - 1;
          const borderRadius =
            isFirst && isLast
              ? '0.375rem'
              : isFirst
                ? '0.375rem 0 0 0.375rem'
                : isLast
                  ? '0 0.375rem 0.375rem 0'
                  : '0';

          return (
            <div
              key={cfg.key}
              className="relative group/seg h-full flex items-center justify-center cursor-default transition-opacity hover:opacity-80"
              style={{ width: `${widthPct}%`, backgroundColor: cfg.hex, borderRadius }}
            >
              {widthPct > 12 && (
                <span className="text-white text-xs font-semibold truncate px-1 select-none pointer-events-none">
                  {cfg.label.toUpperCase()}
                </span>
              )}

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[200] hidden group-hover/seg:block pointer-events-none">
                <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-md shadow-xl whitespace-nowrap">
                  <SegmentTooltip data={data} cfg={cfg} />
                </div>
                {/* Arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-gray-900" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
        {activeSegments.map(cfg => (
          <div key={cfg.key} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: cfg.hex }} />
            <span className="text-[10px] text-gray-500">{cfg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

type ComponentKey = (typeof COMPONENT_CONFIG)[number]['key'];

function BreakdownList({
  data,
  selectedKey,
  onSelectKey,
}: {
  data: ScoreBreakdownData;
  selectedKey: ComponentKey | null;
  onSelectKey: (key: ComponentKey) => void;
}) {
  const total = data.total;
  const labelTotal = useText('score_breakdown.total');

  return (
    <div>
      {COMPONENT_CONFIG.map(cfg => {
        const comp = data.components[cfg.key];
        const weight = comp?.weight ?? 0;
        const Icon = cfg.Icon;
        const isSelected = selectedKey === cfg.key;

        let detail = '';
        if (comp != null) {
          if ((cfg.key === 'curated' || cfg.key === 'biobank') && 'count' in comp) {
            detail =
              comp.count === 0 ? '' : comp.count > 1 ? ` (${comp.count} sources)` : ` (${comp.count} source)`;
          } else if (cfg.key === 'clinical' && 'sources' in comp) {
            detail = comp.sources.length > 0 ? ` (${comp.sources.length} source)` : '';
          } else if (cfg.key === 'literature' && 'publications' in comp) {
            detail = (comp as any).publications > 0 ? ` (${(comp as any).publications} pubs)` : '';
          } else if ((cfg.key === 'models' || cfg.key === 'inferred') && 'count' in comp) {
            detail =
              comp.count === 0
                ? ''
                : comp.count > 1
                  ? ` (${comp.count} sources) `
                  : ` (${comp.count} source) `;
          }
        }

        return (
          <div
            key={cfg.key}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-sm ${weight > 0 ? '' : 'opacity-35'} ${isSelected ? 'bg-gray-50' : ''}`}
          >
            <Icon
              className="w-3.5 h-3.5 flex-shrink-0"
              style={{ color: weight > 0 ? cfg.textHex : '#9CA3AF' }}
            />
            <span className="flex-1 text-xs text-gray-700 min-w-0">
              {cfg.label}
              <span className="text-gray-400">{detail}</span>
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <BaseTooltip
                content={`See ${cfg.key === 'literature' ? 'publications' : 'sources'} for ${cfg.label}`}
                position="top"
                className="flex"
              >
                <button
                  onClick={() => onSelectKey(cfg.key)}
                  className="p-0.5 rounded hover:bg-gray-200 transition-colors pb-0"
                >
                  <GoInfo
                    className="w-3.5 h-3.5 stroke-1"
                    style={{ color: isSelected ? cfg.textHex : '#9CA3AF' }}
                  />
                </button>
              </BaseTooltip>
              <div className="w-14 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: total > 0 ? `${(weight / total) * 100}%` : '0%',
                    backgroundColor: weight > 0 ? cfg.hex : '#D1D5DB',
                  }}
                />
              </div>
              <span
                className="text-xs font-semibold w-8 text-right"
                style={{ color: weight > 0 ? cfg.textHex : '#D1D5DB' }}
              >
                {weight.toFixed(2)}
              </span>
            </div>
          </div>
        );
      })}

      <div className="border-t border-gray-200 my-1 mx-2" />

      <div className="flex items-center gap-2 px-2 pt-1.5">
        <span className="flex-1 text-xs font-bold text-gray-800">Total Score</span>
        <span className="text-sm font-bold text-gray-900 w-8 text-right">{total.toFixed(2)}</span>
      </div>
      <span className="text-xs text-gray-500 ml-2 mt-0 italic">{labelTotal}</span>
    </div>
  );
}

export function ScoreBreakdown({ data }: ScoreBreakdownProps) {
  const [selectedKey, setSelectedKey] = useState<ComponentKey | null>(null);

  const handleSelectKey = (key: ComponentKey) => {
    setSelectedKey(prev => (prev === key ? null : key));
  };

  const selectedCfg = COMPONENT_CONFIG.find(cfg => cfg.key === selectedKey);
  const selectedComp = selectedKey ? data.components[selectedKey] : null;
  const selectedSources = selectedComp && 'sources' in selectedComp ? selectedComp.sources : [];
  const selectedPublications =
    selectedComp && 'publications' in selectedComp ? (selectedComp as any).publications : undefined;

  return (
    <div className="space-y-4">
      <SegmentedBar data={data} />
      <BreakdownList data={data} selectedKey={selectedKey} onSelectKey={handleSelectKey} />

      {selectedCfg && (
        <div className="p-3 bg-gray-50 rounded-md border border-gray-100">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {selectedCfg.label} {selectedSources.length > 0 ? 'Sources' : 'Publications'}
          </p>
          <div className="mt-1">
            {selectedSources.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {selectedSources.map(src => (
                  <SharedBadge key={src} text={src} variant="custom" customColor={selectedCfg.hex} />
                ))}
              </div>
            ) : selectedPublications !== undefined ? (
              <p className="text-[10px] text-gray-500">{selectedPublications} publications</p>
            ) : (
              <p className="text-[10px] text-gray-400">No sources available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
